import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  GIVE_PAGE, PUSHPAY_DIRECT, DEFAULTS as SHEET_DEFAULTS, isSafeGiveUrl, wholeNumber, validGiving, isSupportTurn as supportTurn,
  askState, HOLD_MS, REST_KEY, HOLD_KEY, OUT_KEY, readTime, writeTime,
} from './giveCore';
import { SCRIPTURE_DEFAULTS, validScripture, scriptureMode } from './scripture';
import { ASK_ABOUT_DEFAULTS, validAskAbout } from './quote';

export { isSafeGiveUrl, costSentence } from './giveCore';

/**
 * When (and whether) to mention giving: the policy since the support sheet
 * (Oct 2026).
 *
 * Ask Pastor Rob is free for everyone, and it always will be. People it has
 * helped can support the work, and are told so in a few quiet places:
 *
 *   1. the menu's support line and, on wide screens, the folded rail's heart
 *   2. one muted line on every third question of a window (supportEvery):
 *      under the thinking card while AI Rob answers, then under that answer.
 *      The card that says a window's questions are used up carries it too.
 *   3. one card a window, after the answer that brings the window to
 *      donationCardAt (16) questions: what the AI computing behind their
 *      questions has cost so far, and a Donate button
 *
 * A plain tap on any of them opens the support sheet (components/GiveSheet.js):
 * what a gift does, then Give once and Give monthly, two real links to The
 * Net's Pushpay page (utils/giveCore.js builds them). A modified click, the
 * sheet switched off (giveSheet 0, except in a browser marked as the team's)
 * or no script follows the plain link, giveUrl, as before. A gift unlocks
 * nothing, and the app never learns whether anyone gave.
 *
 * The asks only ever get quieter, on this device:
 *   - a visit to Pushpay, "Not now", closing the sheet, or the card's "Maybe
 *     later": no new line and no card for the rest of that window (the
 *     lines already on screen stay, and so do the menu and the rail);
 *   - "I already give to The Net": the same, for 90 days;
 *   - a crisis card, an answer that offered help (giveCore.offersHelp: 988,
 *     findhelp.org, a hotline), or the server's givingQuiet (the safety
 *     classifier's hold, or a crisis or a hard season in the conversation,
 *     functions/guards.js hardSeasonIn): no line, card or sheet for 24
 *     hours, and the menu shows the 988 line where the ask was.
 * Never in a conversation where a crisis card appeared, or a hard season was
 * told (debt, a death, drinking, a home that hurts), whatever the safety
 * classifier rated it: someone in a hard place is never asked for money.
 * Visitors who arrived from an ad see the same as everyone else.
 *
 * A window is five hours from a person's first question (functions/limits.js);
 * the server counts the questions and says which one this is.
 *
 * The give link, both numbers and the sheet's fields come from
 * meta/publicConfig (scripts/set-public-config.js), read once when the app
 * opens; every answer's metadata carries them too, and that copy wins. No
 * deploy. The rules are giveCore.js's, the same as the server's. The same
 * doc carries the Bible references' switches (utils/scripture.js) and "Ask
 * about this"'s (utils/quote.js), kept here with the rest of it.
 */

export const GIVE_URL = PUSHPAY_DIRECT;

/** The exact words, everywhere they appear. */
export const SUPPORT_LEAD = 'If Ask Pastor Rob has been helpful to you, and you’d like to support the project, you can';

const DEFAULTS = { giveUrl: GIVE_URL, supportEvery: 3, donationCardAt: 16, ...SHEET_DEFAULTS, ...SCRIPTURE_DEFAULTS, ...ASK_ABOUT_DEFAULTS };
const CARD_KEY = 'apr-donation-card'; // the window (its resetAt) the card was last shown in
const INTERNAL_KEY = 'apr-internal'; // a browser marked as the team's (?apr_internal=1, utils/analytics.js)
const LEAVE_KEY = 'apr-give-leave'; // a deliberate same-tab trip to Pushpay (the sheet's fallback link)

/**
 * The sheet's 'Go to Pushpay in this tab' leaves on purpose, and the draft
 * brings the conversation back, so App's unsaved-conversation warning should
 * stay quiet for that one navigation (ten seconds, this tab only).
 */
export function noteDeliberateLeave() {
  try { window.sessionStorage.setItem(LEAVE_KEY, String(Date.now())); } catch (e) { /* the warning shows; harmless */ }
}

export function isDeliberateLeave() {
  try { return Date.now() - Number(window.sessionStorage.getItem(LEAVE_KEY) || 0) < 10000; } catch (e) { return false; }
}

let config = { ...DEFAULTS };
let loadStarted = false;
const listeners = new Set();

/**
 * Takes whatever arrived (meta/publicConfig, or an answer's metadata) that is
 * sane. A field that is missing or bad leaves what the browser has, so an
 * older server's metadata can never switch anything.
 */
export function applyPublicConfig(source) {
  if (!source || typeof source !== 'object') return;
  const next = { ...config, ...validGiving(source), ...validScripture(source), ...validAskAbout(source) };
  if (isSafeGiveUrl(source.giveUrl)) next.giveUrl = source.giveUrl;
  const every = wholeNumber(source.supportEvery, [0, 50]);
  if (every !== null) next.supportEvery = every;
  const cardAt = wholeNumber(source.donationCardAt, [0, 1000]);
  if (cardAt !== null) next.donationCardAt = cardAt;
  if (Object.keys(next).every((key) => next[key] === config[key])) return;
  config = next;
  listeners.forEach((fn) => fn(config));
}

// The web app reads meta/publicConfig itself as the page opens (utils/experiments.js), so the A/B
// tests know their switches by the welcome's first paint, and hands that read over here: it stays
// the page's one read of the document. The phone app never hands one over, and reads as before.
let handedRead = null;

/**
 * A read of meta/publicConfig already under way: a promise of its fields as plain values (null or {}
 * when there is no document). Taken only before the first usePublicConfig; if it fails, getDoc
 * reads the document instead.
 */
export function takePublicConfigRead(read) {
  if (!loadStarted && read && typeof read.then === 'function') handedRead = read;
}

/** Once per page: the public settings, or the defaults if they can't be read. */
function loadPublicConfig() {
  if (loadStarted) return;
  loadStarted = true;
  const viaSdk = () => getDoc(doc(db, 'meta', 'publicConfig'))
    .then((snap) => { if (snap.exists()) applyPublicConfig(snap.data()); });
  const read = handedRead ? handedRead.then((data) => applyPublicConfig(data), viaSdk) : viaSdk();
  read.catch(() => { /* not readable (yet): the defaults stand */ });
}

export function getPublicConfig() {
  return config;
}

export function usePublicConfig() {
  const [value, setValue] = useState(config);
  useEffect(() => {
    loadPublicConfig();
    setValue(config);
    listeners.add(setValue);
    return () => listeners.delete(setValue);
  }, []);
  return value;
}

/** Every supportEvery-th question of a window gets the support line (giveCore.js). */
export function isSupportTurn(windowCount, cfg = config) {
  return supportTurn(windowCount, cfg);
}

/**
 * The first answer to reach donationCardAt questions in a window gets the
 * card (normally the 16th; the 17th if the 16th never finished), once. A
 * window is known by when it ends (resetAt).
 */
export function isDonationTurn(windowCount, resetAt, cfg = config) {
  const n = Number(windowCount);
  if (!(cfg.donationCardAt > 0) || !Number.isInteger(n) || n < cfg.donationCardAt) return false;
  return !donationCardHandled(resetAt);
}

let handledInMemory = null; // when storage is blocked: once per window per page

function donationCardHandled(resetAt) {
  if (!resetAt) return false;
  if (handledInMemory === String(resetAt)) return true;
  try {
    return localStorage.getItem(CARD_KEY) === String(resetAt);
  } catch (e) {
    return false;
  }
}

/**
 * The window's card is spent: shown, or held back because the conversation
 * had a crisis card or the asks were resting (it does not come back later in
 * that window either).
 */
export function markDonationCardHandled(resetAt) {
  if (!resetAt) return;
  handledInMemory = String(resetAt);
  try { localStorage.setItem(CARD_KEY, String(resetAt)); } catch (e) { /* storage blocked: memory keeps it to once */ }
}

// ---------------------------------------------------------------------------
// The support sheet's switch. Off (giveSheet 0), every entry is the plain
// link it always was; a browser marked as the team's sees the sheet anyway,
// so staff can check it on real phones before anyone else does.
// ---------------------------------------------------------------------------

export function sheetOn(cfg = config) {
  if (cfg && cfg.giveSheet === 1) return true;
  try {
    return window.localStorage.getItem(INTERNAL_KEY) === '1';
  } catch (e) {
    return false;
  }
}

/**
 * The Bible references' switch on this device (utils/scripture.js): 0 plain
 * words, 1 a tap opens the passage, 2 the same with a faint dotted line. A
 * browser marked as the team's sees at least 1, the way it sees the sheet.
 */
export function scriptureModeNow(cfg = config) {
  let internal = false;
  try {
    internal = window.localStorage.getItem(INTERNAL_KEY) === '1';
  } catch (e) { /* storage blocked: the setting alone */ }
  return scriptureMode(cfg, internal);
}

// ---------------------------------------------------------------------------
// This device's memory: until when the asks rest, until when a crisis hold
// runs, and when this tab last went to Pushpay. Never sent anywhere. When
// storage is blocked (a private window, cleared site data) this page still
// remembers, for as long as it is open.
// ---------------------------------------------------------------------------

const memory = {}; // only what storage refused to keep
const stateListeners = new Set();

function storageArea(name) {
  try {
    return window[name] || null;
  } catch (e) {
    return null; // reading it can throw when site data is blocked
  }
}

function timeAt(name, key) {
  const stored = readTime(storageArea(name), key);
  return key in memory ? Math.max(stored, memory[key]) : stored;
}

function keepTime(name, key, value) {
  if (writeTime(storageArea(name), key, value)) delete memory[key];
  else memory[key] = value;
}

function stateChanged() {
  stateListeners.forEach((fn) => fn());
}

/** The asks rest until `until`: never sooner than a rest already running. */
export function restGiving(until) {
  const at = Number(until);
  if (!Number.isFinite(at) || at <= timeAt('localStorage', REST_KEY)) return;
  keepTime('localStorage', REST_KEY, at);
  stateChanged();
}

/** A crisis card, or the server's givingQuiet: no line, card or sheet on this device for 24 hours. */
export function holdGiving() {
  const until = Date.now() + HOLD_MS;
  if (until > timeAt('localStorage', HOLD_KEY)) keepTime('localStorage', HOLD_KEY, until);
  stateChanged();
}

/** A tap on Give: when this tab went to Pushpay, for the thank-you after a reload (ChatView). */
export function markGiveOut() {
  keepTime('sessionStorage', OUT_KEY, Date.now());
}

export function readGiveOut() {
  return timeAt('sessionStorage', OUT_KEY);
}

export function clearGiveOut() {
  delete memory[OUT_KEY];
  try {
    const store = storageArea('sessionStorage');
    if (store) store.removeItem(OUT_KEY);
  } catch (e) { /* storage blocked */ }
}

// Every mounted ChatView says whether it is answering, whether its
// conversation had a crisis card, and when its window ends. App keeps the
// archive and a Focused Study conversation mounted together, so the sheet
// waits for either one's answer, and a crisis in either quiets both.
const conversations = new Map();

export function reportConversation(id, { answering = false, crisis = false, resetAt = null } = {}) {
  const next = { answering: Boolean(answering), crisis: Boolean(crisis), resetAt: Number.isFinite(resetAt) ? resetAt : null };
  const was = conversations.get(id);
  if (was && was.answering === next.answering && was.crisis === next.crisis && was.resetAt === next.resetAt) return;
  conversations.set(id, next);
  stateChanged();
}

export function forgetConversation(id) {
  if (conversations.delete(id)) stateChanged();
}

/**
 * Where the asks stand: { ask, answering, resetAt }. ask is 'hold' (a crisis
 * card on screen, or the 24-hour hold), 'rest' or 'ask'; answering is true
 * while any conversation has an answer arriving; resetAt is when the current
 * window ends, if an answer has said.
 */
export function getGivingState(now = Date.now()) {
  const list = [...conversations.values()];
  const ask = list.some((c) => c.crisis)
    ? 'hold'
    : askState(now, { holdUntil: timeAt('localStorage', HOLD_KEY), restUntil: timeAt('localStorage', REST_KEY) });
  const ends = list.map((c) => c.resetAt).filter((t) => t && t > now);
  return { ask, answering: list.some((c) => c.answering), resetAt: ends.length ? Math.max(...ends) : null };
}

export function useGivingState() {
  const [value, setValue] = useState(() => getGivingState());
  useEffect(() => {
    let timer = null;
    const update = () => {
      const next = getGivingState();
      setValue((prev) => (prev.ask === next.ask && prev.answering === next.answering && prev.resetAt === next.resetAt ? prev : next));
      // Look again the moment a rest or a hold runs out.
      clearTimeout(timer);
      const now = Date.now();
      const ends = [timeAt('localStorage', REST_KEY), timeAt('localStorage', HOLD_KEY)].filter((t) => t > now);
      if (ends.length) timer = setTimeout(update, Math.min(Math.min(...ends) - now + 50, 2147483647));
    };
    // Another tab rested or held the asks: this one follows.
    const onStorage = (e) => { if (!e.key || e.key === REST_KEY || e.key === HOLD_KEY) update(); };
    update();
    stateListeners.add(update);
    window.addEventListener('storage', onStorage);
    return () => {
      clearTimeout(timer);
      stateListeners.delete(update);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  return value;
}

// ---------------------------------------------------------------------------
// The open sheet: { surface, opener, limitTime, n }, or null. One GiveSheet
// is mounted (AppShell); every entry opens it through openGiveFromClick.
// ---------------------------------------------------------------------------

let sheet = null;
let opened = 0;
const sheetListeners = new Set();

export function openGiveSheet({ surface, opener = null, limitTime = null } = {}) {
  opened += 1;
  sheet = { surface, opener, limitTime, n: opened };
  sheetListeners.forEach((fn) => fn(sheet));
}

export function closeGiveSheet() {
  if (!sheet) return;
  sheet = null;
  sheetListeners.forEach((fn) => fn(sheet));
}

export function useGiveSheet() {
  const [value, setValue] = useState(sheet);
  useEffect(() => {
    setValue(sheet);
    sheetListeners.add(setValue);
    return () => sheetListeners.delete(setValue);
  }, []);
  return value;
}

/**
 * A tap on a give link (the line, the card's Donate, the menu, the rail).
 * Opens the sheet and returns true; returns false, so the link works as the
 * plain link it is, when the sheet is off, the click is modified or not the
 * main button (a new tab is theirs to open), something else already handled
 * it, or a crisis hold is running. Enter on a link is a click too.
 *   surface    'answer' | 'card' | 'limit' | 'menu' | 'rail'
 *   limitTime  when the window's questions refresh ("4:30 PM"), from the limit card
 */
export function openGiveFromClick(e, { surface, limitTime = null } = {}) {
  if (!e || !sheetOn() || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return false;
  if (getGivingState().ask === 'hold') return false;
  e.preventDefault();
  openGiveSheet({ surface, opener: e.currentTarget || null, limitTime });
  return true;
}
